import React, { useState } from 'react';
import { ArrowUpRight, Github, Mail, ArrowLeft } from 'lucide-react';
import ProjectTopBar from './ProjectTopBar';
import { Reveal } from './ui/Reveal';
import { contactInfo } from '../data/assistant/knowledge';

const btnBase =
  'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors';
const btnPrimary = `${btnBase} bg-[var(--fg-primary)] text-[var(--bg-canvas)] hover:opacity-90`;
const btnSecondary = `${btnBase} border border-[var(--baton-hairline)] text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]`;

/** A labelled band of the case study: small label on the left, content on the right from lg up. */
function Band({ label, title, children, id }) {
  return (
    <section id={id} className="border-t border-[var(--baton-hairline)] py-14 sm:py-20">
      <Reveal className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.6fr)] lg:gap-16">
        <div>
          <p className="baton-mono text-xs uppercase tracking-[0.18em] text-[var(--fg-muted)] lg:sticky lg:top-24">{label}</p>
        </div>
        <div className="min-w-0">
          {title && (
            <h2 className="baton-display text-2xl font-semibold leading-tight tracking-tight text-[var(--fg-primary)] sm:text-3xl">
              {title}
            </h2>
          )}
          <div className={title ? 'mt-6' : ''}>{children}</div>
        </div>
      </Reveal>
    </section>
  );
}

function Prose({ paragraphs }) {
  return (
    <div className="max-w-2xl space-y-5 text-base leading-relaxed text-[var(--fg-secondary)] sm:text-[17px]">
      {paragraphs.map((p) => (
        <p key={p.slice(0, 32)}>{p}</p>
      ))}
    </div>
  );
}

function Gallery({ images, captions, title }) {
  const [active, setActive] = useState(0);
  if (!images?.length) return null;

  return (
    <div className="space-y-3">
      <figure className="overflow-hidden rounded-xl border border-[var(--baton-hairline)] bg-[var(--bg-elevated)]">
        <img
          src={images[active]}
          alt={`${title}: ${captions?.[active] || `screen ${active + 1}`}`}
          className="block w-full h-auto"
          loading="lazy"
        />
        {captions?.[active] && (
          <figcaption className="border-t border-[var(--baton-hairline)] px-4 py-2.5 text-sm text-[var(--fg-secondary)]">
            {captions[active]}
          </figcaption>
        )}
      </figure>
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
          {images.map((img, i) => (
            <button
              key={img}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show ${captions?.[i] || `screen ${i + 1}`}`}
              aria-pressed={active === i}
              className={`aspect-video overflow-hidden rounded-md border transition-opacity cursor-pointer ${
                active === i
                  ? 'border-[var(--fg-primary)]'
                  : 'border-[var(--baton-hairline)] opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Narrative case study layout, used when a project has `caseStudy` data.
 * Describes what the product does and why it exists; implementation details stay out.
 */
export default function CaseStudyPage({ project, onBack }) {
  const cs = project.caseStudy;

  return (
    <div className="baton-scope min-h-screen bg-[var(--bg-canvas)] text-[var(--fg-primary)] flex flex-col">
      <ProjectTopBar project={project} onBack={onBack} label="CASE STUDY" />

      <main className="flex-1 w-full max-w-[1120px] mx-auto px-5 sm:px-6">
        {/* Hero */}
        <header className="pt-14 pb-12 sm:pt-20 sm:pb-16">
          <Reveal>
            <p className="baton-mono text-xs uppercase tracking-[0.18em] text-[var(--fg-muted)]">
              {cs.kind}
            </p>
            <h1 className="baton-display mt-4 text-5xl font-semibold tracking-tight text-[var(--fg-primary)] sm:text-7xl">
              {project.title}
            </h1>
            <p className="baton-display mt-6 max-w-3xl text-2xl font-medium leading-snug text-[var(--fg-primary)] sm:text-[2rem] sm:leading-tight">
              {cs.headline}
            </p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--fg-secondary)] sm:text-lg">
              {cs.intro}
            </p>

            <div className="mt-8 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
              {project.liveUrl && (
                <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className={btnPrimary}>
                  Visit the website <ArrowUpRight size={15} />
                </a>
              )}
              {project.githubUrl && (
                <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className={btnSecondary}>
                  <Github size={15} /> GitHub
                </a>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <dl className="mt-14 grid grid-cols-1 border-t border-[var(--baton-hairline)] sm:grid-cols-2 lg:grid-cols-4">
              {cs.facts.map((f) => (
                <div key={f.label} className="border-b border-[var(--baton-hairline)] py-4 sm:pr-6 lg:border-b-0">
                  <dt className="baton-mono text-[11px] uppercase tracking-wider text-[var(--fg-muted)]">{f.label}</dt>
                  <dd className="mt-1.5 text-sm text-[var(--fg-primary)]">{f.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </header>

        {/* Lead image */}
        {project.images?.[0] && (
          <Reveal className="pb-14 sm:pb-20">
            <div className="overflow-hidden rounded-xl border border-[var(--baton-hairline)] bg-[var(--bg-elevated)]">
              <img
                src={project.images[0]}
                alt={`${project.title}: ${cs.captions?.[0] || 'overview'}`}
                className="block w-full h-auto"
              />
            </div>
          </Reveal>
        )}

        {/* Outcomes */}
        <Band label="Outcomes">
          <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-[var(--baton-hairline)] bg-[var(--baton-hairline)] sm:grid-cols-2">
            {cs.outcomes.map((o) => (
              <div key={o.label} className="flex flex-col-reverse justify-end gap-3 bg-[var(--bg-canvas)] p-6">
                <dt className="max-w-xs text-sm leading-relaxed text-[var(--fg-secondary)]">{o.label}</dt>
                <dd className="baton-display text-4xl font-semibold tracking-tight text-[var(--fg-primary)] tabular-nums sm:text-5xl">
                  {o.value}
                </dd>
              </div>
            ))}
          </dl>
        </Band>

        {/* Story */}
        <Band label="Where it started" title={cs.story.title}>
          <Prose paragraphs={cs.story.paragraphs} />
        </Band>

        {/* Flow */}
        <Band label="How a task moves" title={cs.flow.title}>
          <p className="max-w-2xl text-base leading-relaxed text-[var(--fg-secondary)]">{cs.flow.intro}</p>
          <ol className="mt-8 grid grid-cols-1 border-t border-[var(--baton-hairline)] md:grid-cols-2 xl:grid-cols-4 xl:border-t-0">
            {cs.flow.steps.map((s, i) => (
              <li
                key={s.name}
                className="border-b border-[var(--baton-hairline)] py-6 md:pr-6 xl:border-b-0 xl:border-t xl:pt-6"
              >
                <p className="baton-mono text-[11px] uppercase tracking-wider text-[var(--fg-muted)]">Step {i + 1}</p>
                <h3 className="baton-display mt-2 text-lg font-semibold text-[var(--fg-primary)]">{s.name}</h3>
                <p className="mt-1 text-sm font-medium text-[var(--fg-primary)]">{s.who}</p>
                <p className="mt-2 text-sm leading-relaxed text-[var(--fg-secondary)]">{s.text}</p>
              </li>
            ))}
          </ol>
        </Band>

        {/* Capabilities */}
        {cs.capabilities.map((group) => (
          <Band key={group.label} label={group.label} title={group.title}>
            <dl className="grid grid-cols-1 gap-x-10 border-t border-[var(--baton-hairline)] md:grid-cols-2">
              {group.items.map((item) => (
                <div key={item.title} className="border-b border-[var(--baton-hairline)] py-5">
                  <dt className="text-base font-semibold text-[var(--fg-primary)]">{item.title}</dt>
                  <dd className="mt-1.5 text-sm leading-relaxed text-[var(--fg-secondary)]">{item.text}</dd>
                </div>
              ))}
            </dl>
          </Band>
        ))}

        {/* Results */}
        <Band label="What changed" title={cs.results.title}>
          <Prose paragraphs={cs.results.paragraphs} />
        </Band>

        {/* Screens */}
        {project.images?.length > 1 && (
          <Band label="Screens" title={cs.galleryTitle}>
            <Gallery images={project.images} captions={cs.captions} title={project.title} />
          </Band>
        )}

        {/* Built with */}
        <Band label="Built with">
          <p className="max-w-2xl text-base leading-relaxed text-[var(--fg-primary)]">{cs.stack.join(', ')}.</p>
          {cs.quality && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--fg-secondary)]">{cs.quality}</p>}
        </Band>

        {/* Closing */}
        <section className="border-t border-[var(--baton-hairline)] py-16 sm:py-24">
          <Reveal className="max-w-2xl">
            <h2 className="baton-display text-3xl font-semibold leading-tight tracking-tight text-[var(--fg-primary)] sm:text-4xl">
              {cs.closing.title}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[var(--fg-secondary)] sm:text-lg">{cs.closing.text}</p>
            <div className="mt-8 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
              <a
                href={`mailto:${contactInfo.email}?subject=${encodeURIComponent(`About ${project.title}`)}`}
                className={btnPrimary}
              >
                <Mail size={15} /> Email Rakshan
              </a>
              {project.liveUrl && (
                <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className={btnSecondary}>
                  Visit the website <ArrowUpRight size={15} />
                </a>
              )}
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-[var(--baton-hairline)] py-8 px-5 sm:px-6 text-xs text-[var(--fg-secondary)]">
        <div className="max-w-[1120px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>Rakshan Shetty, {project.title} case study</span>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[var(--fg-primary)] hover:underline cursor-pointer"
          >
            <ArrowLeft size={13} /> Back to all projects
          </button>
        </div>
      </footer>
    </div>
  );
}
